/**
 * Report a render-time failure, by allowlist.
 *
 * `ErrorBoundary` has carried an `onError` prop since it was written — "the seam
 * for wiring a real reporter without touching every call site". This is that
 * reporter. Until now the boundary's only output was `console.error`, which is
 * visible to the one person the page broke for and to nobody else.
 *
 * ## Why this is not `@sentry/react`
 *
 * It emits the Sentry *envelope* format, so it works unchanged against Sentry or
 * a self-hosted GlitchTip — the vendor choice stays open.
 *
 * Two reasons not to take the SDK. It auto-instruments: breadcrumbs capture
 * clicks, navigations, console output and every XHR URL, and switching that off
 * is a **redaction list of what may not be sent**. This module is the other way
 * round — `buildEvent` names each field, and there is no path by which anything
 * else arrives. And the bundle has a hard CI budget with roughly 56 KB gzipped
 * of headroom; the SDK is around 30 KB of it, spent on a reporter rather than on
 * the application.
 *
 * ## What is deliberately not sent
 *
 * **The error message** — it routinely interpolates the value that broke, and on
 * these screens that is a borrower, an account number or a loan reference.
 *
 * **The URL path.** `/loans/SFA-10231` identifies a borrower's case. The
 * boundary's `area` prop ("This page", "Sales analysis") says which part of the
 * app failed without naming a record, so that is sent instead. There is no
 * fallback to `window.location` — the fallback is the leak.
 *
 * **The component stack beyond its first frame**, which is where the throw was,
 * and which is composed of our own component names.
 *
 * Off unless `REACT_APP_ERROR_REPORTER_DSN` is set at build time, and every
 * failure inside here is swallowed: a reporter that throws would replace the
 * error it was reporting, which `ErrorBoundary` already guards against but
 * should not have to.
 */

import { portalEnv } from './portalEnv';
import { BUILD, UNKNOWN } from './buildInfo';

/** Identifies this client to the server. Not the application's version. */
export const CLIENT = 'sf-portal-reporter/1';

/**
 * Build-time variables.
 *
 * Each `process.env.REACT_APP_*` must stay a **literal** reference — CRA inlines
 * them by textual substitution, so `const e = process.env; e.REACT_APP_FOO`
 * reaches the browser as `undefined`. Same rule as `clerkKey.js`.
 *
 * @returns {{dsn: string|undefined, portalEnv: string|undefined}}
 */
const buildTimeVars = () => ({
  dsn: process.env.REACT_APP_ERROR_REPORTER_DSN,
  portalEnv: process.env.REACT_APP_PORTAL_ENV,
});

/**
 * Split a Sentry DSN into its endpoint and public key.
 *
 * Anything that does not parse returns `null`, so a typo disables reporting
 * rather than posting somewhere unintended.
 *
 * @param {string|undefined} raw
 * @returns {{url: string, publicKey: string}|null}
 */
export function parseDsn(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const url = new URL(raw.trim());
    const publicKey = url.username;
    const projectId = url.pathname.replace(/^\/+|\/+$/g, '');
    if (!publicKey || !projectId || !url.host) return null;
    return {
      url: `${url.protocol}//${url.host}/api/${projectId}/envelope/`,
      publicKey,
    };
  } catch {
    return null;
  }
}

/**
 * The first frame of a React component stack.
 *
 * React formats it as repeated "\n    in ComponentName (at File.jsx:12)" lines.
 * The innermost is first and is where the throw happened; the rest is the route
 * down to it, which adds length rather than information.
 *
 * @param {{componentStack?: string}|undefined} info
 * @returns {string|null}
 */
export function topFrame(info) {
  const stack = info && typeof info.componentStack === 'string' ? info.componentStack : '';
  const first = stack.split('\n').map((line) => line.trim()).filter(Boolean)[0];
  return first || null;
}

/**
 * Assemble the payload, field by named field.
 *
 * Nothing is copied wholesale from the error or the page. Every value is placed
 * here explicitly — that is the allowlist, and it is why a field nobody added
 * cannot leak.
 *
 * @param {Error} error
 * @param {{componentStack?: string}} [info]
 * @param {string} [area] the boundary's area, e.g. "Sales analysis"
 * @param {{portalEnv?: string, release?: string}} [context]
 * @returns {object} a Sentry event
 */
export function buildEvent(error, info, area, context = {}) {
  const env = portalEnv(context.portalEnv);
  const name = (error && error.name) || 'Error';
  const frame = topFrame(info);

  return {
    event_id: randomId(),
    timestamp: new Date().toISOString(),
    platform: 'javascript',
    level: 'error',
    // A tag, so an `unknown` environment is visible in the monitor rather than
    // silently filed as production.
    environment: env,
    release: context.release || null,
    logger: 'sf-portal-frontend',
    // Type and location, never the message.
    exception: {
      values: [{ type: name, module: frame }],
    },
    // The boundary's area, never the URL — see the module comment.
    transaction: area || null,
    tags: { environment: env, area: area || null, exception_type: name },
    fingerprint: [name, area || '', frame || 'unknown'],
  };
}

/** A 32-character hex id, using crypto when the browser offers it. */
export function randomId() {
  try {
    const bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    let out = '';
    while (out.length < 32) out += Math.floor(Math.random() * 16).toString(16);
    return out.slice(0, 32);
  }
}

/**
 * UTF-8 byte length of a string.
 *
 * The envelope's item header declares the payload's length in BYTES, so
 * `string.length` is wrong the moment a name carries an accent. `TextEncoder` is
 * universal in browsers but absent from the jsdom the unit suite runs in, so the
 * fallback is not defensive padding — without it this module could not be tested
 * at all.
 *
 * @param {string} text
 * @returns {number}
 */
export function byteLength(text) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(text).length;
  }
  return unescape(encodeURIComponent(text)).length;
}

/**
 * The Sentry envelope: three newline-delimited JSON documents.
 *
 * @param {object} event
 * @param {string} dsn
 * @returns {string}
 */
export function buildEnvelope(event, dsn) {
  const body = JSON.stringify(event);
  const header = JSON.stringify({
    event_id: event.event_id,
    sent_at: new Date().toISOString(),
    dsn,
  });
  const item = JSON.stringify({
    type: 'event',
    content_type: 'application/json',
    length: byteLength(body),
  });
  return `${header}\n${item}\n${body}`;
}

/** @param {string} publicKey */
export function authHeader(publicKey) {
  return `Sentry sentry_version=7, sentry_client=${CLIENT}, sentry_key=${publicKey}`;
}

/**
 * Report one render failure. Returns whether anything was sent.
 *
 * Never throws and never rejects: the caller is already handling a failure.
 *
 * @param {Error} error
 * @param {{componentStack?: string}} [info]
 * @param {string} [area]
 * @param {{vars?: object, transport?: Function, release?: string}} [options]
 * @returns {Promise<boolean>}
 */
export async function reportRenderError(error, info, area, options = {}) {
  try {
    const vars = options.vars || buildTimeVars();
    const parsed = parseDsn(vars.dsn);
    if (!parsed) return false;

    // `BUILD` is `"unknown"` when nothing stamped the bundle. Send null rather
    // than the literal string, so an unstamped build is absent from the report
    // instead of being grouped under a release named "unknown".
    const release =
      options.release !== undefined ? options.release : BUILD === UNKNOWN ? null : BUILD;

    const envelope = buildEnvelope(
      buildEvent(error, info, area, { portalEnv: vars.portalEnv, release }),
      vars.dsn,
    );

    const send = options.transport || ((url, init) => fetch(url, init));
    await send(parsed.url, {
      method: 'POST',
      body: envelope,
      // Survives the navigation or reload the operator is about to perform.
      keepalive: true,
      headers: {
        'Content-Type': 'application/x-sentry-envelope',
        'X-Sentry-Auth': authHeader(parsed.publicKey),
      },
    });
    return true;
  } catch {
    return false;
  }
}

export default reportRenderError;
